import React from 'react';
import { motion } from 'framer-motion';
import { Headphones, MessageCircle, Users, Clock, CheckCircle, AlertCircle } from 'lucide-react';

const SupportBridgeWorkspace: React.FC = () => {
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="p-3 border-b border-divider flex-shrink-0">
        <h3 className="text-lg font-medium violett-gradient-text text-drop-shadow">Support Bridge</h3>
        <p className="text-sm text-text-muted mt-1">Customer Support &amp; Service Operations</p>
      </div>
      
      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          <motion.div 
            className="glass-dark rounded-xl p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <MessageCircle className="w-6 h-6 text-text-muted" />
              <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Active Tickets</h4>
            </div>
            <div className="space-y-3">
              <div className="p-3 bg-surface-hover rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">#2024-001 - Login Issues</span>
                  <span className="text-xs bg-red-500/20 text-red-400 px-2 py-1 rounded">High</span>
                </div>
                <p className="text-sm text-text-muted mt-1">User unable to access workspace after password reset</p>
                <div className="flex items-center gap-2 mt-2 text-xs text-text-muted">
                  <Clock className="w-3 h-3" />
                  <span>2 hours ago</span>
                </div>
              </div>
              <div className="p-3 bg-surface-hover rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium">#2024-002 - Feature Request</span>
                  <span className="text-xs bg-surface-hover text-text-muted px-2 py-1 rounded">Medium</span>
                </div>
                <p className="text-sm text-text-muted mt-1">Request for additional workspace customization options</p>
                <div className="flex items-center gap-2 mt-2 text-xs text-text-muted">
                  <Clock className="w-3 h-3" />
                  <span>4 hours ago</span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="glass-dark rounded-xl p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Headphones className="w-6 h-6 text-green-400" />
              <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Live Chat Status</h4>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-surface-hover rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="font-medium">Chat Queue</span>
                </div>
                <span className="text-sm font-medium text-green-400">3 active</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-surface-hover rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <span className="font-medium">Waiting</span>
                </div>
                <span className="text-sm font-medium text-yellow-400">1 pending</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-surface-hover rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-text-muted rounded-full"></div>
                  <span className="font-medium">Average Response</span>
                </div>
                <span className="text-sm font-medium text-text-muted">2.3 min</span>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="glass-dark rounded-xl p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Users className="w-6 h-6 text-accent" />
              <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Support Team</h4>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-surface-hover rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-slate-500 rounded-full flex items-center justify-center text-xs font-bold">
                    SA
                  </div>
                  <div>
                    <div className="font-medium">Sarah Anderson</div>
                    <div className="text-xs text-text-muted">Senior Support Agent</div>
                  </div>
                </div>
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              </div>
              <div className="flex items-center justify-between p-3 bg-surface-hover rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-accent to-pink-500 rounded-full flex items-center justify-center text-xs font-bold">
                    MJ
                  </div>
                  <div>
                    <div className="font-medium">Mike Johnson</div>
                    <div className="text-xs text-text-muted">Technical Support</div>
                  </div>
                </div>
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              </div>
              <div className="flex items-center justify-between p-3 bg-surface-hover rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-xs font-bold">
                    LC
                  </div>
                  <div>
                    <div className="font-medium">Lisa Chen</div>
                    <div className="text-xs text-text-muted">Customer Success</div>
                  </div>
                </div>
                <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="glass-dark rounded-xl p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-cyan-400" />
              <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Performance Metrics</h4>
            </div>
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">Resolution Rate</span>
                  <span className="text-sm font-medium text-green-400">94.2%</span>
                </div>
                <div className="w-full bg-surface-hover rounded-full h-2">
                  <div className="bg-gradient-to-r from-green-400 to-emerald-500 h-2 rounded-full" style={{ width: '94.2%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">Customer Satisfaction</span>
                  <span className="text-sm font-medium text-text-muted">4.8/5</span>
                </div>
                <div className="w-full bg-surface-hover rounded-full h-2">
                  <div className="bg-gradient-to-r from-text-muted to-accent h-2 rounded-full" style={{ width: '96%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm">First Contact Resolution</span>
                  <span className="text-sm font-medium text-cyan-400">87%</span>
                </div>
                <div className="w-full bg-surface-hover rounded-full h-2">
                  <div className="bg-gradient-to-r from-cyan-400 to-text-muted h-2 rounded-full" style={{ width: '87%' }}></div>
                </div>
              </div>
            </div>
          </motion.div>

        </div>

        <motion.div 
          className="mt-6 glass-dark rounded-xl p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-6 h-6 text-orange-400" />
            <h4 className="text-lg font-semibold cream-gradient-text text-drop-shadow">Recent Activity</h4>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-3 p-3 bg-surface-hover rounded-lg">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span className="text-sm">Ticket #2024-001 escalated to technical team</span>
              <span className="text-xs text-text-muted ml-auto">5 min ago</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-surface-hover rounded-lg">
              <div className="w-2 h-2 bg-text-muted rounded-full"></div>
              <span className="text-sm">New customer onboarding session completed</span>
              <span className="text-xs text-text-muted ml-auto">12 min ago</span>
            </div>
            <div className="flex items-center gap-3 p-3 bg-surface-hover rounded-lg">
              <div className="w-2 h-2 bg-accent rounded-full"></div>
              <span className="text-sm">Knowledge base article updated: "Workspace Setup"</span>
              <span className="text-xs text-text-muted ml-auto">25 min ago</span>
            </div>
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default SupportBridgeWorkspace;
